import { useState } from 'react';
import { Outlet, useNavigate, useOutletContext } from 'react-router';
import { useTranslation } from 'react-i18next';
import { History, Home, MessageSquare, Receipt, UserRound, Wrench } from 'lucide-react';
import type { Property } from '@vuokramani/shared';
import { AppShell } from '../../components/AppShell.tsx';
import type { NavItem } from '../../components/Sidebar.tsx';
import { Button } from '../../components/ui.tsx';
import { useThreadCounts } from '../../lib/messages.ts';
import { propertyTitle, useProperties } from '../../lib/properties.ts';
import { NewThreadDialog } from '../shared/Messages.tsx';
import s from './tenant.module.css';

const ITEMS: NavItem[] = [
  { to: '/vuokralainen/asunto', labelKey: 'nav.property', icon: Home },
  { to: '/vuokralainen/laskut', labelKey: 'nav.invoices', icon: Receipt },
  { to: '/vuokralainen/viestit', labelKey: 'nav.messages', icon: MessageSquare },
  { to: '/vuokralainen/historia', labelKey: 'nav.history', icon: History },
  { to: '/vuokralainen/tiedot', labelKey: 'nav.settings', icon: UserRound },
];

interface TenantContext {
  properties: Property[];
  current: Property | undefined;
  isLoading: boolean;
}

/**
 * Vuokralainen: yksinkertainen näkymä. Jos vuokra-asuntoja on useampi, sivupaneelin
 * ylälaitaan tulee asunnon valinta.
 */
export function TenantShell() {
  const { t } = useTranslation();
  const { data: properties = [], isLoading } = useProperties('tenant');
  const [selectedId, setSelectedId] = useState<string>();
  const [faultOpen, setFaultOpen] = useState(false);
  const navigate = useNavigate();
  const { data: counts } = useThreadCounts('party');
  const current = properties.find((p) => p.id === selectedId) ?? properties[0];
  // Asunnon asetukset: laskut voivat olla piilossa vuokralaiselta.
  const items = ITEMS.filter(
    (item) => item.to !== '/vuokralainen/laskut' || current?.settings.tenantInvoices !== false,
  ).map((item) =>
    item.to === '/vuokralainen/viestit' ? { ...item, badge: counts?.unreadThreads } : item,
  );

  const selector =
    properties.length > 1 ? (
      <label className={s.selector}>
        <span className={s.selectorLabel}>{t('tenant.choose')}</span>
        <select value={current?.id} onChange={(e) => setSelectedId(e.target.value)}>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {propertyTitle(p)}
            </option>
          ))}
        </select>
      </label>
    ) : undefined;

  const faultButton = current?.settings.tenantMessages ? (
    <Button
      variant="secondary"
      onClick={() => setFaultOpen(true)}
      aria-label={t('topbar.faultReport')}
    >
      <Wrench size={16} aria-hidden="true" />
      <span className="hide-mobile">{t('topbar.faultReport')}</span>
    </Button>
  ) : undefined;

  return (
    <AppShell items={items} home="/vuokralainen" sidebarTop={selector} topbarActions={faultButton}>
      <Outlet context={{ properties, current, isLoading } satisfies TenantContext} />
      {faultOpen && current && (
        <NewThreadDialog
          as="party"
          kind="fault"
          propertyId={current.id}
          onClose={() => setFaultOpen(false)}
          onCreated={(thread) => {
            setFaultOpen(false);
            void navigate(`/vuokralainen/viestit/${thread.id}`);
          }}
        />
      )}
    </AppShell>
  );
}

export function useTenant(): TenantContext {
  return useOutletContext<TenantContext>();
}
